import { useState } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { syncService, type SyncResult } from '@/features/sync/syncService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';

export function SyncStatusButton() {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: lastSync } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => syncService.getSyncStatus(),
    refetchInterval: syncing ? 5000 : false,
  });

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    try {
      const result = await syncService.triggerSync();
      setSyncResult(result);
      await queryClient.invalidateQueries({ queryKey: ['search'] });
      await queryClient.invalidateQueries({ queryKey: ['map-stations'] });
      await queryClient.invalidateQueries({ queryKey: ['sync-status'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Senkronizasyon başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const handleStreamCheck = async () => {
    setSyncing(true);
    setError(null);
    try {
      await syncService.checkAllStreams();
      await queryClient.invalidateQueries({ queryKey: ['map-stations'] });
      await queryClient.invalidateQueries({ queryKey: ['search'] });
      await queryClient.invalidateQueries({ queryKey: ['sync-status'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Stream kontrol başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const isRunning = syncing || lastSync?.status === 'running';

  return (
    <div className="pointer-events-auto flex items-center gap-2 rounded-xl glass p-1.5">
      <button
        type="button"
        onClick={handleSync}
        disabled={isRunning}
        aria-label="İstasyonları senkronize et"
        title="İstasyonları senkronize et"
        className={clsx(
          'flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
          isRunning
            ? 'text-slate-500'
            : 'text-slate-300 hover:bg-white/10 hover:text-slate-100',
        )}
      >
        {isRunning ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" />
        )}
        <span className="hidden sm:inline">
          {isRunning ? 'Senkronize ediliyor...' : 'Senkronize'}
        </span>
      </button>

      <button
        type="button"
        onClick={handleStreamCheck}
        disabled={isRunning}
        aria-label="Stream sağlık kontrolü"
        title="Stream sağlık kontrolü"
        className={clsx(
          'flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
          isRunning
            ? 'text-slate-500'
            : 'text-slate-300 hover:bg-white/10 hover:text-slate-100',
        )}
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        <span className="hidden lg:inline">Stream kontrol</span>
      </button>

      {syncResult && !syncing && (
        <span className="flex items-center gap-1 text-xs text-emerald-400">
          <CheckCircle2 className="h-3 w-3" />
          +{syncResult.stationsAdded} / ~{syncResult.stationsUpdated}
        </span>
      )}

      {error && !syncing && (
        <span
          className="flex max-w-64 items-center gap-1 text-xs text-red-400"
          role="alert"
          title={error}
        >
          <AlertCircle className="h-3 w-3" />
          <span className="truncate">{error}</span>
        </span>
      )}

      {lastSync?.finishedAt && !syncing && !syncResult && (
        <span className="hidden text-2xs text-slate-500 md:inline">
          {formatRelative(lastSync.finishedAt)}
        </span>
      )}
    </div>
  );
}

function formatRelative(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'az önce';
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  return `${days} gün önce`;
}
